import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Linkedin } from 'lucide-react';
import { cn } from '../../lib/utils';
import DataWyseLogo from '../../assets/datawyse_logo_marian.svg';
import Analytics from '../../lib/analytics';

const footerLinks = {
  product: [
    { label: 'Features', href: '#product' },
    { label: 'Use Cases', href: '#use-cases' },
    { label: 'Getting Started', href: '#getting-started' },
    { label: 'FAQ', href: '#faq' },
  ],
  company: [
    { label: 'Careers', href: '/careers' },
    { label: 'Contact', href: '/contact' },
  ],
};

const socialLinks = [
  {
    icon: Linkedin,
    href: 'https://www.linkedin.com/company/datawyseai',
    label: 'LinkedIn',
  },
];

interface FooterSectionProps {
  className?: string;
}

export const FooterSection: React.FC<FooterSectionProps> = ({ className }) => {
  // Handle footer link clicks
  const handleFooterLinkClick = (linkText: string, destination: string) => {
    Analytics.trackFooter('footer_link_click', {
      link_text: linkText,
      destination: destination,
    });
  };

  // Handle route navigation with scroll to top
  const handleRouteNavigation = (linkText: string, destination: string) => {
    handleFooterLinkClick(linkText, destination);
    // Scroll to top after navigation
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  // Handle social media clicks
  const handleSocialClick = (platform: string, url: string) => {
    Analytics.trackFooter('footer_social_click', {
      platform: platform.toLowerCase(),
      link_text: platform,
      destination: url,
    });
  };

  // Handle contact info clicks
  const handleContactClick = (contactType: 'email' | 'phone') => {
    Analytics.trackFooter('footer_contact_click', {
      contact_type: contactType,
      link_text:
        contactType === 'email' ? 'info@datawyse.in' : '+91 9895588100',
    });
  };

  return (
    <footer
      id="company"
      className={cn(
        'relative overflow-hidden bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-blue-100',
        className
      )}
    >
      {/* Decorative background elements */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute top-0 right-0 h-96 w-96 rounded-full bg-white/5 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 h-80 w-80 rounded-full bg-blue-400/10 blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 h-64 w-64 rounded-full bg-indigo-400/5 blur-2xl"></div>
      </div>

      <div className="relative z-10 w-full">
        {/* Main footer content */}
        <div className="container mx-auto px-4 py-8 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
            {/* Company info - compact */}
            <div>
              <div className="mb-4">
                <div className="mb-3 flex items-center justify-center space-x-2 lg:justify-start">
                  <img
                    src={DataWyseLogo}
                    alt="DataWyse Logo"
                    className="h-10"
                  />
                </div>
                <p className="font-primary mb-3 text-center text-sm leading-relaxed text-blue-200 lg:text-left">
                  Transforming businesses through intelligent data solutions.
                </p>
              </div>

              <div className="font-primary flex flex-col items-center space-y-2 text-xs lg:items-start">
                <a
                  href="mailto:info@datawyse.in"
                  className="group flex items-center"
                  onClick={() => handleContactClick('email')}
                >
                  <div className="mr-2 flex h-6 w-6 items-center justify-center rounded bg-white/10 transition-colors group-hover:bg-white/20">
                    <Mail className="h-3 w-3 text-blue-200" />
                  </div>
                  <span className="text-blue-100">info@datawyse.in</span>
                </a>
              </div>
            </div>

            {/* Product column */}
            <div className="text-center lg:text-left">
              <h4 className="font-primary mb-4 text-lg font-bold text-white">
                Product
              </h4>
              <ul className="space-y-3">
                {footerLinks.product.map(link => (
                  <li key={link.label}>
                    <button
                      onClick={() => {
                        handleFooterLinkClick(link.label, link.href);
                        const targetId = link.href.replace('#', '');
                        const scrollToSection = (window as any).scrollToSection;
                        if (scrollToSection) {
                          scrollToSection(targetId);
                        } else {
                          // Fallback for other pages - navigate to landing page with hash
                          window.location.href = `/${link.href}`;
                        }
                      }}
                      className="font-primary cursor-pointer border-none bg-transparent p-0 text-left text-sm text-blue-200 transition-colors duration-200 hover:text-white"
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company column */}
            <div className="text-center lg:text-left">
              <h4 className="font-primary mb-4 text-lg font-bold text-white">
                Company
              </h4>
              <ul className="space-y-3">
                {footerLinks.company.map(link => (
                  <li key={link.label}>
                    {link.href.startsWith('/') ? (
                      <Link
                        to={link.href}
                        className="font-primary text-sm text-blue-200 transition-colors duration-200 hover:text-white"
                        onClick={() =>
                          handleRouteNavigation(link.label, link.href)
                        }
                      >
                        {link.label}
                      </Link>
                    ) : (
                      <a
                        href={link.href}
                        className="font-primary text-sm text-blue-200 transition-colors duration-200 hover:text-white"
                        onClick={() =>
                          handleFooterLinkClick(link.label, link.href)
                        }
                      >
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Social links column */}
            <div className="text-center lg:text-left">
              <h4 className="font-primary mb-4 text-lg font-bold text-white">
                Follow Us
              </h4>
              <div className="flex flex-col items-center space-y-3 lg:items-start">
                {socialLinks.map(social => {
                  const IconComponent = social.icon;
                  return (
                    <a
                      key={social.label}
                      href={social.href}
                      className="flex items-center space-x-2 text-blue-200 transition-colors duration-300 hover:text-white"
                      aria-label={social.label}
                      onClick={() =>
                        handleSocialClick(social.label, social.href)
                      }
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 transition-all duration-300 hover:bg-white/20">
                        <IconComponent className="h-4 w-4" />
                      </div>
                      <span className="font-primary text-sm">
                        {social.label}
                      </span>
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/20 pt-6 pb-4">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center">
              <p className="font-primary text-base text-blue-200">
                © 2025 Wyse Intelligence. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
